import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Inbox, ShieldCheck } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@/components/button"
import { RefreshButton } from "@/components/shared/refresh-button"
import { StatusAlert } from "@/components/shared/status-alert"
import { ToastStack, type AppToast } from "@/components/shared/toast-stack"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { SkeletonTable } from "@/components/ui/skeleton"
import { formatGuildDateTime } from "@/lib/guild-date-format"
import { guildWarApi } from "@/lib/guild-war-api"
import { toGuildWarPathDisplayLabel } from "@/lib/guild-war-path-display"
import { guildWarKeys } from "@/lib/query-keys"

export const Route = createFileRoute(
  "/_authed/_admin/guild-war/permanent-membership",
)({
  component: PermanentMembershipPage,
})

type ToastMessage = AppToast

const pendingMembershipApplicationsQueryKey =
  guildWarKeys.pendingPermanentMembershipApplications()

type ReviewDecision = "approved" | "rejected"

function PermanentMembershipPage() {
  const queryClient = useQueryClient()
  const [pendingByApplicationId, setPendingByApplicationId] = useState<
    Record<string, ReviewDecision>
  >({})
  const [pendingRevokeById, setPendingRevokeById] = useState<
    Record<string, boolean>
  >({})
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const [tableDensity, setTableDensity] = useState<"comfortable" | "compact">(
    "comfortable",
  )
  const reviewNoteDraftsRef = useRef<Record<string, string>>({})

  const permanentMembersQuery = useQuery({
    queryKey: guildWarKeys.permanentMembershipMembers(),
    queryFn: guildWarApi.listPermanentMembers,
  })

  const applicationsQuery = useQuery({
    queryKey: pendingMembershipApplicationsQueryKey,
    queryFn: () =>
      guildWarApi.listPermanentMembershipApplications({
        state: "pending",
      }),
  })

  const pushToast = (tone: ToastMessage["tone"], text: string) => {
    const id = Date.now() + Math.floor(Math.random() * 1000)
    setToasts((prev) => [...prev, { id, tone, text }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((item) => item.id !== id))
    }, 3000)
  }

  const dismissToast = (toastId: number) => {
    setToasts((prev) => prev.filter((item) => item.id !== toastId))
  }

  const reviewMutation = useMutation({
    mutationFn: ({
      applicationId,
      payload,
    }: {
      applicationId: string
      payload: {
        decision: ReviewDecision
        reviewNote?: string
      }
    }) =>
      guildWarApi.reviewPermanentMembershipApplication(applicationId, payload),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: pendingMembershipApplicationsQueryKey,
      })

      const previous = queryClient.getQueryData(
        pendingMembershipApplicationsQueryKey,
      )

      queryClient.setQueryData(
        pendingMembershipApplicationsQueryKey,
        (
          current:
            | Awaited<
                ReturnType<
                  typeof guildWarApi.listPermanentMembershipApplications
                >
              >
            | undefined,
        ) => {
          if (!current) {
            return current
          }

          return current.filter((item) => item.id !== variables.applicationId)
        },
      )

      return { previous }
    },
    onSuccess: (_data, variables) => {
      if (variables.payload.decision === "rejected") {
        delete reviewNoteDraftsRef.current[variables.applicationId]
      }
      pushToast(
        "success",
        `Membership application ${variables.payload.decision}`,
      )
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          pendingMembershipApplicationsQueryKey,
          context.previous,
        )
      }

      const msg =
        error instanceof Error
          ? error.message
          : "Failed to review membership application"
      pushToast("error", msg)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: pendingMembershipApplicationsQueryKey,
      })
      void queryClient.invalidateQueries({
        queryKey: guildWarKeys.permanentMembershipMembers(),
      })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: ({ membershipId }: { membershipId: string }) =>
      guildWarApi.revokePermanentMembership(membershipId),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: guildWarKeys.permanentMembershipMembers(),
      })
      const previous = queryClient.getQueryData(
        guildWarKeys.permanentMembershipMembers(),
      )
      queryClient.setQueryData(
        guildWarKeys.permanentMembershipMembers(),
        (current: PermanentMember[] | undefined) => {
          if (!current) return current
          return current.filter(
            (m) => m.membershipId !== variables.membershipId,
          )
        },
      )
      return { previous }
    },
    onSuccess: () => pushToast("success", "Permanent membership revoked"),
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          guildWarKeys.permanentMembershipMembers(),
          context.previous,
        )
      }
      const msg =
        error instanceof Error
          ? error.message
          : "Failed to revoke permanent membership"
      pushToast("error", msg)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: guildWarKeys.permanentMembershipMembers(),
      })
      void queryClient.invalidateQueries({
        queryKey: guildWarKeys.pendingPermanentMembershipApplications(),
      })
    },
  })

  const handleReview = async (
    applicationId: string,
    payload: {
      decision: ReviewDecision
      reviewNote?: string
    },
  ) => {
    setPendingByApplicationId((prev) => ({
      ...prev,
      [applicationId]: payload.decision,
    }))

    try {
      await reviewMutation.mutateAsync({ applicationId, payload })
    } finally {
      setPendingByApplicationId((prev) => {
        const next = { ...prev }
        delete next[applicationId]
        return next
      })
    }
  }

  const handleApprove = (applicationId: string) => {
    void handleReview(applicationId, {
      decision: "approved",
    })
  }

  const handleReject = (applicationId: string) => {
    const reviewNote = reviewNoteDraftsRef.current[applicationId]?.trim()
    void handleReview(applicationId, {
      decision: "rejected",
      reviewNote: reviewNote || undefined,
    })
  }

  const handleRevoke = async (membershipId: string) => {
    setPendingRevokeById((prev) => ({ ...prev, [membershipId]: true }))
    try {
      await revokeMutation.mutateAsync({ membershipId })
    } finally {
      setPendingRevokeById((prev) => {
        const next = { ...prev }
        delete next[membershipId]
        return next
      })
    }
  }

  const getReviewNoteDraft = useCallback((applicationId: string) => {
    return reviewNoteDraftsRef.current[applicationId] ?? ""
  }, [])

  const setReviewNoteDraft = useCallback(
    (applicationId: string, reviewNote: string) => {
      if (!reviewNote.trim()) {
        delete reviewNoteDraftsRef.current[applicationId]
        return
      }

      reviewNoteDraftsRef.current[applicationId] = reviewNote
    },
    [],
  )

  const handleRefreshAll = () => {
    void permanentMembersQuery.refetch()
    void applicationsQuery.refetch()
  }

  const isAnyFetching =
    permanentMembersQuery.isFetching || applicationsQuery.isFetching

  return (
    <>
      <div className="grid gap-4">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle>Permanent Membership</CardTitle>
                <CardDescription>
                  Track active permanent members and review new permanent
                  membership applications in one place
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={
                    tableDensity === "comfortable" ? "default" : "outline"
                  }
                  onClick={() => setTableDensity("comfortable")}
                >
                  Comfortable table
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={tableDensity === "compact" ? "default" : "outline"}
                  onClick={() => setTableDensity("compact")}
                >
                  Compact table
                </Button>
                <RefreshButton
                  isFetching={isAnyFetching}
                  onClick={handleRefreshAll}
                />
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Permanent Members</CardTitle>
            <CardDescription>
              {permanentMembersQuery.data
                ? `${permanentMembersQuery.data.length} active permanent members`
                : "Loading active permanent members"}
            </CardDescription>
          </CardHeader>
          <CardContent aria-busy={permanentMembersQuery.isLoading}>
            {permanentMembersQuery.isLoading ? (
              <>
                <p className="sr-only" role="status">
                  Loading permanent members table
                </p>
                <SkeletonTable rows={4} columns={4} aria-hidden="true" />
              </>
            ) : null}
            {permanentMembersQuery.isError ? (
              <StatusAlert tone="danger">
                Failed to load permanent members.
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2"
                  onClick={() => void permanentMembersQuery.refetch()}
                >
                  Retry
                </Button>
              </StatusAlert>
            ) : null}

            {permanentMembersQuery.data?.length ? (
              <div className="overflow-x-auto">
                <table
                  className={`data-table text-left text-sm ${tableDensity === "compact" ? "[&_:is(th,td)]:px-2.5 [&_:is(th,td)]:py-2" : ""}`}
                >
                  <caption className="sr-only">
                    Active permanent members
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Primary Skill Path</th>
                      <th scope="col">Secondary Skill Path</th>
                      <th scope="col">State</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permanentMembersQuery.data.map((member) => (
                      <PermanentMemberRow
                        key={member.memberId}
                        member={member}
                        onRevoke={handleRevoke}
                        isRevoking={Boolean(
                          pendingRevokeById[member.membershipId],
                        )}
                        compact={tableDensity === "compact"}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : permanentMembersQuery.isSuccess ? (
              <div className="grid gap-2 rounded-2xl border border-dashed border-border/70 bg-card/35 p-4 text-center">
                <div className="mx-auto flex size-9 items-center justify-center rounded-full border border-primary/25 bg-primary/8 text-primary">
                  <ShieldCheck className="size-4" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  No permanent members found
                </p>
                <p className="text-xs text-muted-foreground">
                  Approved membership applications will appear here as active
                  roster locks.
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pending Membership Applications</CardTitle>
            <CardDescription>
              {applicationsQuery.data
                ? `${applicationsQuery.data.length} membership applications waiting on review`
                : "Loading pending membership applications"}
            </CardDescription>
          </CardHeader>
          <CardContent aria-busy={applicationsQuery.isLoading}>
            {applicationsQuery.isLoading ? (
              <>
                <p className="sr-only" role="status">
                  Loading pending membership applications table
                </p>
                <SkeletonTable rows={3} columns={7} aria-hidden="true" />
              </>
            ) : null}
            {applicationsQuery.isError ? (
              <StatusAlert tone="danger">
                Failed to load pending membership applications.
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2"
                  onClick={() => void applicationsQuery.refetch()}
                >
                  Retry
                </Button>
              </StatusAlert>
            ) : null}

            {applicationsQuery.data?.length ? (
              <div className="overflow-x-auto">
                <table
                  className={`data-table text-left text-sm ${tableDensity === "compact" ? "[&_:is(th,td)]:px-2.5 [&_:is(th,td)]:py-2" : ""}`}
                >
                  <caption className="sr-only">
                    Pending permanent membership applications with review
                    controls
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Primary Skill Path</th>
                      <th scope="col">Secondary Skill Path</th>
                      <th scope="col">Created At</th>
                      <th scope="col">Expires At</th>
                      <th scope="col">Admin Note</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applicationsQuery.data.map((application) => (
                      <PermanentApplicationRow
                        key={application.id}
                        application={application}
                        pendingDecision={pendingByApplicationId[application.id]}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        getReviewNoteDraft={getReviewNoteDraft}
                        setReviewNoteDraft={setReviewNoteDraft}
                        compact={tableDensity === "compact"}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : applicationsQuery.isSuccess ? (
              <div className="grid gap-2 rounded-2xl border border-dashed border-border/70 bg-card/35 p-4 text-center">
                <div className="mx-auto flex size-9 items-center justify-center rounded-full border border-primary/25 bg-primary/8 text-primary">
                  <Inbox className="size-4" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  No pending membership applications
                </p>
                <p className="text-xs text-muted-foreground">
                  New permanent membership requests will queue here for review.
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <ToastStack
        toasts={toasts}
        dismissToast={dismissToast}
        className="md:w-96"
      />
    </>
  )
}

type PermanentApplication = Awaited<
  ReturnType<typeof guildWarApi.listPermanentMembershipApplications>
>[number]

type PermanentMember = Awaited<
  ReturnType<typeof guildWarApi.listPermanentMembers>
>[number]

const formatPrimarySkillPath = (value: string | null | undefined) => {
  return toGuildWarPathDisplayLabel(value, {
    variant: "short",
    fallback: value ?? "-",
  })
}

const formatSecondarySkillPath = (value: string | null | undefined) => {
  if (!value) {
    return "None"
  }

  return toGuildWarPathDisplayLabel(value, {
    variant: "short",
    fallback: value,
  })
}

function PermanentMemberRow({
  member,
  onRevoke,
  isRevoking,
  compact,
}: {
  member: PermanentMember
  onRevoke: (membershipId: string) => void
  isRevoking: boolean
  compact: boolean
}) {
  return (
    <tr>
      <th
        scope="row"
        className="max-w-[200px] truncate font-medium"
        title={member.guildDisplayName || undefined}
      >
        {member.guildDisplayName || "-"}
      </th>
      <td>{formatPrimarySkillPath(member.primaryPathCode)}</td>
      <td>{formatSecondarySkillPath(member.secondaryPathCode)}</td>
      <td>{member.state}</td>
      <td>
        <AlertDialog>
          <AlertDialogTrigger
            render={
              <Button
                variant="destructive"
                size={compact ? "xs" : "sm"}
                disabled={isRevoking}
                aria-busy={isRevoking}
              >
                {isRevoking ? "Revoking..." : "Revoke"}
              </Button>
            }
          />
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke Permanent Membership?</AlertDialogTitle>
              <AlertDialogDescription>
                This will revoke the permanent membership for{" "}
                <span className="font-semibold text-foreground">
                  {member.guildDisplayName || "this member"}
                </span>
                . This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                disabled={isRevoking}
                onClick={() => {
                  onRevoke(member.membershipId)
                }}
              >
                {isRevoking ? "Revoking..." : "Revoke"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </td>
    </tr>
  )
}

function PermanentApplicationRow({
  application,
  pendingDecision,
  onApprove,
  onReject,
  getReviewNoteDraft,
  setReviewNoteDraft,
  compact,
}: {
  application: PermanentApplication
  pendingDecision?: ReviewDecision
  onApprove: (applicationId: string) => void
  onReject: (applicationId: string) => void
  getReviewNoteDraft: (applicationId: string) => string
  setReviewNoteDraft: (applicationId: string, reviewNote: string) => void
  compact: boolean
}) {
  const [reviewNoteDraft, setReviewNoteDraftLocal] = useState(() =>
    getReviewNoteDraft(application.id),
  )

  useEffect(() => {
    setReviewNoteDraftLocal(getReviewNoteDraft(application.id))
  }, [application.id, getReviewNoteDraft])

  const isPending = Boolean(pendingDecision)

  return (
    <tr>
      <th
        scope="row"
        className="max-w-[200px] truncate font-medium"
        title={application.guildDisplayName || undefined}
      >
        {application.guildDisplayName || "-"}
      </th>
      <td>{formatPrimarySkillPath(application.skillPathAtApply)}</td>
      <td>{formatSecondarySkillPath(null)}</td>
      <td className="text-muted-foreground">
        {formatGuildDateTime(application.createdAt)}
      </td>
      <td className="text-muted-foreground">
        {formatGuildDateTime(application.expiresAt)}
      </td>
      <td>
        <Input
          value={reviewNoteDraft}
          onBlur={() => setReviewNoteDraft(application.id, reviewNoteDraft)}
          onChange={(event) => setReviewNoteDraftLocal(event.target.value)}
          placeholder="Optional admin note"
          className={compact ? "h-8" : undefined}
        />
      </td>
      <td>
        <div className="flex gap-2">
          <Button
            size={compact ? "xs" : "sm"}
            disabled={isPending}
            aria-busy={isPending}
            onClick={() => onApprove(application.id)}
          >
            {pendingDecision === "approved" ? "Approving..." : "Approve"}
          </Button>
          <Button
            size={compact ? "xs" : "sm"}
            variant="destructive"
            disabled={isPending}
            aria-busy={isPending}
            onClick={() => {
              setReviewNoteDraft(application.id, reviewNoteDraft)
              onReject(application.id)
            }}
          >
            {pendingDecision === "rejected" ? "Rejecting..." : "Reject"}
          </Button>
        </div>
      </td>
    </tr>
  )
}
